import type { PsaAisleSplitSuggestion } from "@CommonUtils/psa/aisleSplitGap";
import { useState } from "react";
import { type AisleBreakTarget, useDeclareAisleBreakMutation } from "src/api/aisleBreaks";
import { useToasts } from "src/components/Toasts";
import { Button, Color, Flex, Text } from "src/elements";
import { useTranslation } from "src/i18n";

type CommonT = ReturnType<typeof useTranslation<"common">>["t"];

/**
 * States what the app does with the seam; a PSA declares no break, so nothing is set from it.
 * `gap` is in the PSA's own unit, `gapUnit`; without one the number is shown bare, not guessed.
 */
export const describeAisleSplitSuggestion = (t: CommonT, suggestion: PsaAisleSplitSuggestion) => {
  const pair = { left: suggestion.afterBayNo, right: suggestion.afterBayNo + 1 };
  if (suggestion.evidence !== "gap") return t("aisleBreaks.suggestion.unmergedSeam", pair);

  const gap = suggestion.gap.toFixed(1);
  // Optional at runtime: a server older than the field sends gap suggestions without it.
  const gapUnit: string | undefined = suggestion.gapUnit;
  return gapUnit === "cm" || gapUnit === "in"
    ? t("aisleBreaks.suggestion.gap", {
        ...pair,
        gap,
        unit: t(
          gapUnit === "cm"
            ? "aisleBreaks.suggestion.units.centimetres"
            : "aisleBreaks.suggestion.units.inches",
        ),
      })
    : t("aisleBreaks.suggestion.gapWithoutUnit", { ...pair, gap });
};

interface BreakTheAisleHereProps {
  target: AisleBreakTarget;
  afterBayNo: number;
  onDeclared: (afterBayNo: number) => void;
  isDisabled?: boolean;
}

const BreakTheAisleHere = ({
  target,
  afterBayNo,
  onDeclared,
  isDisabled = false,
}: BreakTheAisleHereProps) => {
  const { t } = useTranslation("common");
  const { toast } = useToasts();
  const declare = useDeclareAisleBreakMutation();

  const onClick = () => {
    if (isDisabled) return;
    declare.mutate(
      { ...target, afterBayNo },
      {
        onSuccess: () => onDeclared(afterBayNo),
        onError: (error) =>
          toast({ title: t("aisleBreaks.suggestion.failed", { bayNo: afterBayNo }), error }),
      },
    );
  };

  return (
    <Button
      size="small"
      variant="inverted"
      isLoading={declare.isPending}
      isDisabled={isDisabled}
      onClick={onClick}
    >
      {t("aisleBreaks.suggestion.breakHere")}
    </Button>
  );
};

interface Props {
  suggestions: readonly PsaAisleSplitSuggestion[];
  /** The store the seams belong to; without one (the dev renderer) the lines offer no button. */
  target?: AisleBreakTarget;
  onDeclared?: (afterBayNo: number) => void;
  /** Holds the buttons, e.g. while a save that would overwrite a new break is in flight. */
  isDisabled?: boolean;
  /** The seams the canvas breaks now; a break removed there is no longer declared. */
  breaksAfterBayNos?: readonly number[];
}

export const AisleSplitSuggestions = ({
  suggestions,
  target,
  onDeclared,
  isDisabled,
  breaksAfterBayNos,
}: Props) => {
  const { t } = useTranslation("common");
  // Kept here, not read back from `suggestions`: the refetch drops a declared seam from them.
  const [declaredHere, setDeclaredHere] = useState<number[]>([]);
  const declared = breaksAfterBayNos
    ? declaredHere.filter((afterBayNo) => breaksAfterBayNos.includes(afterBayNo))
    : declaredHere;

  const lines = [
    ...suggestions
      .filter((suggestion) => !declared.includes(suggestion.afterBayNo))
      .map((suggestion) => ({ afterBayNo: suggestion.afterBayNo, suggestion })),
    ...declared.map((afterBayNo) => ({ afterBayNo, suggestion: null })),
  ].sort((a, b) => a.afterBayNo - b.afterBayNo);

  if (lines.length === 0) return null;

  const onLineDeclared = (afterBayNo: number) => {
    setDeclaredHere((current) =>
      current.includes(afterBayNo) ? current : [...current, afterBayNo],
    );
    onDeclared?.(afterBayNo);
  };

  return (
    <Flex column gap="4px">
      {lines.map(({ afterBayNo, suggestion }) =>
        suggestion === null ? (
          <Text key={afterBayNo} variant="small2" color={Color.primaryActive}>
            {t("aisleBreaks.suggestion.declared", { bayNo: afterBayNo })}
          </Text>
        ) : (
          <Flex key={afterBayNo} align="center" gap="8px">
            <Text variant="small2" color={Color.textSecondary}>
              {describeAisleSplitSuggestion(t, suggestion)}
            </Text>

            {target && (
              <BreakTheAisleHere
                target={target}
                afterBayNo={afterBayNo}
                onDeclared={onLineDeclared}
                isDisabled={isDisabled}
              />
            )}
          </Flex>
        ),
      )}
    </Flex>
  );
};
